import React, { useEffect, useState } from "react";
import Main from "./Main";
import AppBreadcrumbs from "../components/ui/AppBreadcrumbs";
import useBankVerification from "../hooks/useBankVerification";
import { searchMatmCustomers } from "../api/finkedaApi";

const MatmBankVerification = () => {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [customersLoading, setCustomersLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const verification = useBankVerification({
    sourceType: "MATM",
    sourceId: customerId,
  });

  useEffect(() => {
    let active = true;
    searchMatmCustomers({ page: 1, limit: 50 })
      .then((response) => {
        if (!active) return;
        if (!response.success) {
          setLoadError(response.message || "Unable to load MATM customers");
          return;
        }
        const rows = response.data?.items || [];
        setCustomers(
          rows.filter(
            (row) => !row.isDummy && /^[a-f\d]{24}$/i.test(String(row.id || ""))
          )
        );
      })
      .finally(() => {
        if (active) setCustomersLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const result = verification.result;
  const statusLabel = result?.verificationBypassed
    ? "Verification Bypassed"
    : result?.verificationStatus === "VERIFIED"
      ? "Bank Verified"
      : result?.verificationStatus === "PENDING"
        ? "Verification Pending"
        : result?.verificationStatus === "FAILED"
          ? "Verification Failed"
          : "Verify Bank";

  return (
    <Main showRightPanel={false}>
      <AppBreadcrumbs
        items={[
          { label: "Dashboard", to: "/" },
          { label: "MATM" },
          { label: "Bank Verification" },
        ]}
      />

      <section className="ds-card mt-4 w-full max-w-3xl p-4 sm:p-6">
        <h1 className="text-base font-semibold text-[var(--text-dark)]">
          MATM Bank Verification
        </h1>
        <p className="mt-1 text-sm text-[var(--text-muted)]">
          Select the exact MATM customer record. Verification status and charges
          are stored separately from customer details.
        </p>

        <label className="mt-5 flex flex-col gap-1 text-sm text-[var(--text-muted)]">
          MATM customer
          <select
            className="ds-input"
            value={customerId}
            onChange={(event) => setCustomerId(event.target.value)}
            disabled={customersLoading}
          >
            <option value="">
              {customersLoading ? "Loading customers…" : "Select a MATM customer"}
            </option>
            {customers.map((customer) => (
              <option key={customer.id} value={customer.id}>
                {customer.name} · {customer.mobileMasked || "Mobile unavailable"} ·{" "}
                {customer.accountMasked || "Account unavailable"}
              </option>
            ))}
          </select>
        </label>

        {loadError && (
          <p className="mt-2 text-sm text-red-700" role="alert">
            {loadError}
          </p>
        )}
        {!customersLoading && !loadError && customers.length === 0 && (
          <p className="mt-2 text-sm text-[var(--text-muted)]" role="status">
            No MATM customer records are available to verify.
          </p>
        )}

        {verification.verificationRequired && (
          <div className="mt-4">
            <button
              type="button"
              className="ds-action-btn"
              onClick={() => verification.verify()}
              disabled={
                !customerId ||
                verification.loading ||
                verification.statusLoading ||
                verification.policyLoading ||
                verification.pending ||
                verification.providerUnavailable ||
                verification.alreadyVerified
              }
            >
              {verification.loading
                ? "Verifying…"
                : verification.pending
                  ? "Verification Pending"
                  : verification.alreadyVerified
                    ? "Bank Verified"
                    : "Verify Bank"}
            </button>
            {verification.policy?.pennyDropCharge?.enabled && (
              <p className="mt-2 text-xs text-[var(--text-muted)]">
                Penny Drop charge: ₹{verification.policy.pennyDropCharge.amount}.
              </p>
            )}
          </div>
        )}

        {customerId && (
          <div
            className={`mt-5 rounded-lg border p-4 ${
              result?.verificationStatus === "VERIFIED"
                ? "border-green-300 bg-green-50 text-green-800"
                : result?.verificationStatus === "PENDING"
                  ? "border-amber-300 bg-amber-50 text-amber-900"
                  : result?.verificationStatus === "FAILED"
                    ? "border-red-300 bg-red-50 text-red-800"
                    : "border-[var(--border-color)] text-[var(--text-muted)]"
            }`}
            role="status"
          >
            <p className="font-semibold">{statusLabel}</p>
            {result?.message && <p className="mt-1">{result.message}</p>}
            {result?.failureReason && (
              <p className="mt-1">Reason: {result.failureReason}</p>
            )}
            {result?.provider && <p className="mt-1">Provider: {result.provider}</p>}
            {result?.transactionId && (
              <p className="mt-1 break-all text-xs">
                Transaction ID: {result.transactionId}
              </p>
            )}
            {result?.chargeAmount > 0 && (
              <p className="mt-1 text-xs">
                Charge: ₹{result.chargeAmount} ({result.chargeStatus})
              </p>
            )}
          </div>
        )}
      </section>
    </Main>
  );
};

export default MatmBankVerification;
